import { z } from 'zod';
import {
  StoryHistoryArchiveStateSchema,
  StoryHistoryStateSchema,
  StoryVectorMemoryStateSchema,
} from '@/IsekaiRPG/modules/history/historyTypes';
import { ConversationChoiceOptionSchema, ConversationPendingIntentSchema } from '@/IsekaiRPG/modules/chatInteraction/chatInteractionTypes';
import { GameMessageRole } from './chat';

// ============================================
// 对话系统持久化类型
// ============================================

const DEFAULT_CONVERSATION_RUNTIME_SESSION_STATE = {
  sessions: {},
} as const;

const DEFAULT_STORY_HISTORY_STATE = StoryHistoryStateSchema.parse({
  entries: [],
});

const DEFAULT_STORY_HISTORY_ARCHIVE_STATE = StoryHistoryArchiveStateSchema.parse({
  records: [],
});

const DEFAULT_STORY_VECTOR_MEMORY_STATE = StoryVectorMemoryStateSchema.parse({
  records: [],
});

export const ConversationRuntimeSessionMessageSchema = z.object({
  id: z.string(),
  role: GameMessageRole,
  content: z.string(),
  createdAt: z.number(),
  gameTime: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ConversationRuntimeSessionMessage = z.infer<typeof ConversationRuntimeSessionMessageSchema>;

export const ConversationRuntimeChoiceStateSchema = z.object({
  sourceMessageId: z.string(),
  options: z.array(ConversationChoiceOptionSchema).default([]),
});
export type ConversationRuntimeChoiceState = z.infer<typeof ConversationRuntimeChoiceStateSchema>;

export const ConversationRuntimeSessionSchema = z.object({
  id: z.string(),
  sessionKey: z.string(),
  participantIds: z.array(z.string()).default([]),
  participantNames: z.array(z.string()).default([]),
  focusCharacterId: z.string(),
  sceneName: z.string(),
  draftInput: z.string().default(''),
  choiceState: ConversationRuntimeChoiceStateSchema.nullable().default(null),
  pendingIntent: ConversationPendingIntentSchema.nullable().default(null),
  startedAt: z.number(),
  updatedAt: z.number(),
});
export type ConversationRuntimeSession = z.infer<typeof ConversationRuntimeSessionSchema>;

export const ConversationRuntimeSessionStateSchema = z.object({
  sessions: z.record(z.string(), ConversationRuntimeSessionSchema).default({}),
});
export type ConversationRuntimeSessionState = z.infer<typeof ConversationRuntimeSessionStateSchema>;

export const ConversationPersistenceStateSchema = z
  .object({
    conversationRuntimeSession: ConversationRuntimeSessionStateSchema.optional(),
    storyHistory: StoryHistoryStateSchema.optional(),
    storyHistoryArchive: StoryHistoryArchiveStateSchema.optional(),
    storyVectorMemory: StoryVectorMemoryStateSchema.optional(),
  })
  .transform(value => ({
    conversationRuntimeSession: ConversationRuntimeSessionStateSchema.parse(
      value.conversationRuntimeSession ?? DEFAULT_CONVERSATION_RUNTIME_SESSION_STATE,
    ),
    storyHistory: StoryHistoryStateSchema.parse(value.storyHistory ?? DEFAULT_STORY_HISTORY_STATE),
    storyHistoryArchive: StoryHistoryArchiveStateSchema.parse(
      value.storyHistoryArchive ?? DEFAULT_STORY_HISTORY_ARCHIVE_STATE,
    ),
    storyVectorMemory: StoryVectorMemoryStateSchema.parse(value.storyVectorMemory ?? DEFAULT_STORY_VECTOR_MEMORY_STATE),
  }));
export type ConversationPersistenceState = z.infer<typeof ConversationPersistenceStateSchema>;
